'use client'

import { MouseEvent, useCallback, useLayoutEffect, useMemo, useRef } from 'react'

import type { NavGroup } from '@graftcode/design-system'
import { Button, Icon, Navbar, SideNavigation, WorkspaceSelect } from '@graftcode/design-system'
import { useShallow } from 'zustand/react/shallow'

import { SIDEBAR_TEST_IDS } from '@common/common-test-ids.const'
import { Logo } from '@common/components/Logo'
import { getNavigationItems } from '@common/config/navigation'
import { MenuActionId } from '@common/enums'

import { DEFAULT_WORKSPACE_NAME } from '@apps'
import { usePortalOnboardingTargetAnnotations } from '@apps/portal-onboarding'
import { selectWorkspacesPickerState } from '@modules/workspaces/stores/workspaces.selector'
import { useWorkspacesStore } from '@modules/workspaces/stores/workspaces.store'
import { selectLayoutSidebarState } from '@stores/layout/layout.selector'
import { useLayoutStore } from '@stores/layout/layout.store'

import { createWorkspaceItems, getInitials, getWorkspaceLogoUrl } from './Sidebar.helpers'
import type { SidebarProps } from './Sidebar.types'
import { applySidebarNavTestIds } from './utils/applySidebarNavTestIds'

import styles from './Sidebar.module.scss'

const EMPTY_MENU_GROUPS: NavGroup[] = []

export function Sidebar({
  selectedNavId,
  menuGroups = EMPTY_MENU_GROUPS,
  hideNavItemIcons = false,
  navbarTopSlot,
  navbarBottomSlot,
  workspaceName = DEFAULT_WORKSPACE_NAME,
  workspaceLabel = DEFAULT_WORKSPACE_NAME,
  workspaceAvatarSrc = 'DE',
  showInviteButton = false,
  inviteButtonText = '',
  inviteButtonDisabled = false,
  showWorkspaceSelector = true,
  workspaceSelectorLoading = false,
  workspaceSelectorDisabled = false,
  navbarHidden = false,
  logoHref = '/',
  children,
  isCollapsable = true,
  onInviteClick,
  onWorkspaceSelect,
  onNavigationClick,
}: SidebarProps) {
  const { sidebarCollapsed, toggleSidebar } = useLayoutStore(useShallow(selectLayoutSidebarState))
  const { workspaces, currentWorkspace } = useWorkspacesStore(useShallow(selectWorkspacesPickerState))
  const sideNavContainerRef = useRef<HTMLDivElement>(null)
  const navbarContainerRef = useRef<HTMLDivElement>(null)

  usePortalOnboardingTargetAnnotations({
    sideNavContainerRef,
    navbarContainerRef,
  })

  const workspaceItems = useMemo(
    () => createWorkspaceItems(workspaces, currentWorkspace),
    [workspaces, currentWorkspace]
  )

  const displayWorkspaceName = useMemo(() => currentWorkspace?.name || workspaceName, [currentWorkspace, workspaceName])

  const displayWorkspaceLabel = useMemo(
    () => currentWorkspace?.name || workspaceLabel,
    [currentWorkspace, workspaceLabel]
  )

  const displayWorkspaceAvatarFallback = useMemo(
    () => (currentWorkspace ? getInitials(currentWorkspace.name) : workspaceAvatarSrc),
    [currentWorkspace, workspaceAvatarSrc]
  )

  const displayWorkspaceAvatarSrc = useMemo(() => getWorkspaceLogoUrl(currentWorkspace), [currentWorkspace])

  const handleCollapsedToggle = useCallback(() => {
    if (isCollapsable) {
      toggleSidebar()
    }
  }, [toggleSidebar, isCollapsable])

  const navigationItems = useMemo(() => getNavigationItems(selectedNavId), [selectedNavId])

  const handleNavbarNavigationCapture = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (!onNavigationClick) return

      const target = event.target as HTMLElement | null
      const anchor = target?.closest('a[href]') as HTMLAnchorElement | null
      const href = anchor?.getAttribute('href')

      if (!href) return
      if (/^(https?:|mailto:|tel:|#|javascript:)/i.test(href)) return

      const url = new URL(href, window.location.origin)
      if (url.origin !== window.location.origin) return

      const pathSegments = url.pathname.split('/').filter(Boolean)
      if (pathSegments.length === 0) return

      const localeSegment = pathSegments[0]
      const routeSegments = localeSegment?.length === 2 ? pathSegments.slice(1) : pathSegments
      const route = routeSegments.join('/')
      if (!route) return

      event.preventDefault()
      event.stopPropagation()
      onNavigationClick(route)
    },
    [onNavigationClick]
  )

  const renderNavbarBottom = useCallback(() => {
    if (navbarBottomSlot) {
      return navbarBottomSlot
    }

    return (
      <>
        {showInviteButton && (
          <Button
            className={styles.inviteButton}
            size="l"
            variant="outlined"
            text={inviteButtonText}
            icon={Icon.PeopleAddRegular}
            iconOnly={sidebarCollapsed}
            disabled={inviteButtonDisabled}
            onClick={onInviteClick}
            data-testid={SIDEBAR_TEST_IDS.inviteButton}
          />
        )}
        {showWorkspaceSelector && (
          <div
            data-testid={SIDEBAR_TEST_IDS.workspaceSelector}
            style={{
              pointerEvents: workspaceSelectorDisabled ? 'none' : undefined,
              opacity: workspaceSelectorDisabled ? 0.5 : undefined,
            }}
          >
            <WorkspaceSelect
              name={displayWorkspaceName}
              label={displayWorkspaceLabel}
              avatarSrc={displayWorkspaceAvatarSrc}
              avatarFallback={displayWorkspaceAvatarFallback}
              iconOnly={sidebarCollapsed}
              loading={workspaceSelectorLoading}
              options={workspaceItems}
              onSelect={(itemId: string) => {
                const isActionId = (Object.values(MenuActionId) as string[]).includes(itemId)
                const workspaceId =
                  !isActionId && itemId.startsWith('workspace-') ? itemId.replace('workspace-', '') : itemId
                onWorkspaceSelect?.(workspaceId)
              }}
            />
          </div>
        )}
      </>
    )
  }, [
    navbarBottomSlot,
    showInviteButton,
    inviteButtonText,
    inviteButtonDisabled,
    sidebarCollapsed,
    onInviteClick,
    showWorkspaceSelector,
    workspaceSelectorLoading,
    workspaceSelectorDisabled,
    displayWorkspaceName,
    displayWorkspaceLabel,
    displayWorkspaceAvatarSrc,
    displayWorkspaceAvatarFallback,
    workspaceItems,
    onWorkspaceSelect,
  ])

  const navbarClassName = useMemo(() => {
    const classes = [styles.navPanel]
    if (hideNavItemIcons) {
      classes.push(styles.navPanelNoIcons)
    }
    return classes.join(' ')
  }, [hideNavItemIcons])

  useLayoutEffect(() => {
    const container = navbarContainerRef.current
    if (!container || navbarHidden) return

    const apply = () => applySidebarNavTestIds(container, menuGroups ?? EMPTY_MENU_GROUPS)

    apply()

    const observer = new MutationObserver(apply)
    observer.observe(container, { childList: true, subtree: true })

    return () => observer.disconnect()
  }, [menuGroups, navbarHidden, sidebarCollapsed])

  return (
    <div
      className={`${styles.wrapper} ${sidebarCollapsed ? styles.collapsed : ''}`}
      data-testid={SIDEBAR_TEST_IDS.root}
    >
      <header className={styles.header}>
        <a href={logoHref} className={styles.logo}>
          <Logo hideLabel={sidebarCollapsed} showAlpha />
        </a>

        {isCollapsable && (
          <div className={styles.toggleIcon}>
            <Button
              iconOnly
              size="l"
              variant="ghost"
              iconPlacement="right"
              icon={Icon.PanelLeftRegular}
              onClick={handleCollapsedToggle}
            />
          </div>
        )}
      </header>

      <div className={styles.panelContent}>
        <div ref={sideNavContainerRef}>
          <SideNavigation selectedId={selectedNavId} items={navigationItems} />
        </div>
        {!navbarHidden && (
          <div
            ref={navbarContainerRef}
            className={styles.navPanelWrapper}
            onClickCapture={handleNavbarNavigationCapture}
          >
            <Navbar
              className={navbarClassName}
              groups={menuGroups ?? EMPTY_MENU_GROUPS}
              collapsed={sidebarCollapsed}
              slotTop={navbarTopSlot}
              slotBottom={renderNavbarBottom()}
            >
              {children}
            </Navbar>
          </div>
        )}
      </div>
    </div>
  )
}
